/**
 * Pure helpers around `ChildSheetsOut` (`GET /chouette/children/{id}/sheets`)
 * — polling state and the candidate/chosen split — kept out of
 * `src/pages/ChildAppearance.tsx` so they're unit-testable without mounting
 * a component, same convention as `lib/jobProgress.ts` for the story
 * generation screen.
 *
 * A request takes roughly a minute and the API gives no fractional progress
 * for it (unlike `AiJob.progress_done`), so the screen shows a calm waiting
 * state instead of a fake percentage (plan, lot 2) — there is deliberately
 * no "fraction" helper here to mirror.
 *
 * Revision (lot 2, scope change): one request now yields exactly ONE
 * candidate sheet (not three), and the allowance is five requests per
 * appearance (not three) — see `latestCandidate`/`otherCandidates` below
 * for the "one big proposal plus a discreet history row" split this drives
 * on `src/pages/ChildAppearance.tsx`.
 */
import type { Child, ChildSheet, ChildSheetsOut } from "./types";

/** Whether the screen should keep polling `GET .../sheets`. */
export function shouldPollSheets(state: ChildSheetsOut | null | undefined): boolean {
  return !!state?.generating;
}

/** The sheet already picked by the parent, if any. */
export function chosenSheet(sheets: ChildSheet[]): ChildSheet | null {
  return sheets.find((s) => s.status === "chosen") ?? null;
}

/** Sheets on offer, in the order the backend returns them (newest batch
 *  first — see `ChouetteChildSheet.version.desc()` server-side). */
export function candidateSheets(sheets: ChildSheet[]): ChildSheet[] {
  return sheets.filter((s) => s.status === "candidate");
}

/**
 * Whether "Regénérer" should be offered: not while a request is already
 * running, and only while the allowance for the current appearance isn't
 * exhausted (plan §4 lot 1, revised: five requests total per appearance).
 */
export function canRequestNewBatch(state: ChildSheetsOut | null | undefined): boolean {
  return !!state && !state.generating && state.batches_left > 0;
}

/**
 * A chosen sheet whose `matches_appearance` has gone false (the parent
 * edited the description, or the child's age changed, after choosing it) —
 * the screen then offers a fresh batch instead of silently keeping a stale
 * character. `null` when there's nothing chosen, or the chosen sheet is
 * still current.
 */
export function staleChosenSheet(child: Pick<Child, "sheet"> | null | undefined): Child["sheet"] | null {
  const sheet = child?.sheet;
  return sheet && !sheet.matches_appearance ? sheet : null;
}

/** True once the child has a saved guided description at all — gates
 *  whether "L'apparence de <prénom>" opens straight on the form or on the
 *  sheets it already produced. */
export function hasAppearance(child: Pick<Child, "appearance"> | null | undefined): boolean {
  return !!child?.appearance;
}

/** Candidates made from the CURRENT description. Candidates left over from
 *  an older description stay listed by the API (`matches_appearance`
 *  false) but would only confuse the choice, so the screen hides them. */
export function currentCandidates(sheets: ChildSheet[]): ChildSheet[] {
  return candidateSheets(sheets).filter((s) => s.matches_appearance);
}

/**
 * Whether saving the description should launch a request straight away
 * (plan, lot 2: "after saving, launch a request"). Not when one is already
 * running, not once the allowance is used, and not when a sheet for this
 * exact description already exists: re-saving an unchanged description must
 * not burn one of the five requests.
 */
export function shouldAutoStartBatch(state: ChildSheetsOut | null | undefined): boolean {
  if (!canRequestNewBatch(state)) return false;
  return !state!.sheets.some((s) => s.matches_appearance);
}

/** i18n key for an error thrown by the appearance/sheets endpoints (the
 *  message is the API's `detail`, see `apiFetch`). */
export function sheetErrorKey(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error);
  switch (message) {
    case "companion_detail_refused":
      return "appearance.errors.detailRefused";
    case "companion_kind_other_missing":
      return "appearance.errors.companionKindOtherMissing";
    case "companion_kind_refused":
      return "appearance.errors.companionKindRefused";
    case "child_detail_refused":
      return "appearance.errors.childDetailRefused";
    case "sheet_batches_exhausted":
      return "appearance.errors.batchesExhausted";
    case "sheets_already_generating":
      return "appearance.errors.alreadyGenerating";
    case "appearance_missing":
      return "appearance.errors.appearanceMissing";
    default:
      return "appearance.errors.generic";
  }
}

/** The most recently generated candidate for the CURRENT description — the
 *  one shown large by default (each request now yields exactly one sheet,
 *  see the backend's revised `child_sheet.py`: no more 3-per-batch). `null`
 *  once there is none yet. */
export function latestCandidate(sheets: ChildSheet[]): ChildSheet | null {
  return currentCandidates(sheets)[0] ?? null;
}

/** Every current-description candidate except the one currently displayed
 *  large — the discreet "Propositions précédentes" row, so a parent who
 *  preferred an earlier drawing can still reach and choose it. Order
 *  preserved (backend's newest-first). */
export function otherCandidates(sheets: ChildSheet[], displayedId: string | null): ChildSheet[] {
  return currentCandidates(sheets).filter((s) => s.id !== displayedId);
}

/** Whether to show the single animated "still drawing" placeholder card:
 *  exactly while a request is running (one sheet per request now, so there
 *  is never more than one placeholder — plan, lot 2 revision). */
export function pendingSheetPlaceholders(generating: boolean): number {
  return generating ? 1 : 0;
}

/** A sheet is 1536x1024: the child three times (front, three-quarter,
 *  profile), then the doudou twice, on one ground line. */
export const SHEET_ASPECT = 1536 / 1024;
/**
 * Geometry of the square Settings thumbnail, cropped on the front pose (the
 * leftmost figure, the child facing the viewer). `object-fit: cover` alone
 * cannot zoom (on a square it shows 2/3 of the width, three figures), so
 * the image is sized and offset instead.
 *
 * Defaults measured on the 12 real lot 0 sheets: the front pose is centred
 * around x = 0.14 of the width and is wider than a fifth, so a square of
 * 0.24 of the width centred on (0.14, 0.33) frames every face.
 *
 * Returns CSS percentages relative to the square box: the image's width and
 * its left/top offsets, clamped so the crop never runs past the sheet.
 */
export function frontPoseCrop({
  size = 0.24,
  centerX = 0.14,
  centerY = 0.33,
}: { size?: number; centerX?: number; centerY?: number } = {}): { width: string; left: string; top: string } {
  const scale = 1 / size; // image width, in box widths
  const imageHeight = scale / SHEET_ASPECT; // in box heights (the box is square)
  const left = Math.min(0, Math.max(1 - scale, 0.5 - centerX * scale));
  const top = Math.min(0, Math.max(1 - imageHeight, 0.5 - centerY * imageHeight));
  const pct = (n: number) => `${Math.round(n * 1000) / 10}%`;
  return { width: pct(scale), left: pct(left), top: pct(top) };
}
